import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Link,
  Tabs, // Added Tabs
  Tab,  // Added Tab
  IconButton, // Added IconButton
  ListItemButton, // Added ListItemButton
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useThemeColors } from '../theme/useThemeColors';
import {
  AutoAwesome as AutoAwesomeIcon,
  Download as DownloadIcon, // Added DownloadIcon
  PlayArrow as PlayArrowIcon, // Added PlayArrowIcon
  ContentCopy as ContentCopyIcon, // Added ContentCopyIcon for copy link
  Delete as DeleteIcon, // Added DeleteIcon for delete functionality
  Close as CloseIcon, // Added CloseIcon for dismiss button
} from '@mui/icons-material';
import { chartDataService } from '../services/chartDataService';
import { splitAtDelimiter } from '../utils/textSplitting';
import { SERVER_BASE_URL, getDefaultOutroDuration, getDefaultOutroAudioUrl, getVoLanguage, setVoLanguage, VoLanguage } from '../config';
import { useGoogleSheets } from '../context/GoogleSheetsContext';
import { ValidationErrorModal, ValidationError } from './ValidationErrorModal';

// Constants for required Intro/Outro fields
const REQUIRED_INTRO_OUTRO_FIELDS: { [key: string]: string } = {
  'carousel_video1_url': 'Carousel Video 1 URL',
  'carousel_video1_seek': 'Carousel Video 1 Seek',
  'carousel_video2_url': 'Carousel Video 2 URL',
  'carousel_video2_seek': 'Carousel Video 2 Seek',
  'outro_video1_url': 'Outro Video 1 URL',
  'outro_video1_seek': 'Outro Video 1 Seek',
};

const API_BASE_URL = SERVER_BASE_URL;
const DISPLAY_ORDER = ['Tease', 'Intro', '10', '9', '8', '7', '6', '5', '4', '3', '2', '1', 'Outro'];



interface OutputManagerProps {
  spreadsheetId: string; // This is the Weekly Sheet ID passed as a prop
}

export const OutputManager: React.FC<OutputManagerProps> = ({ spreadsheetId: activeSpreadsheetId }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { currentChartConfig, chartCategory } = useGoogleSheets();
  const { themeColor, themeHoverColor } = useThemeColors();

  // Bilingual: which language to render. Mirrors VOManager's per-sheet selector
  // (same localStorage key), so the export renders whatever language the editor is
  // showing. Only surfaced for bilingual charts. Drives the export job's language.
  const isBilingual = !!currentChartConfig?.bilingual;
  const [activeLanguage, setActiveLanguageState] = useState<VoLanguage>(() => getVoLanguage(activeSpreadsheetId));

  // Core State Declarations
  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [outputFormat, setOutputFormat] = useState<string[]>([]);
  
  // VO state variables
  const [voInPoints, setVoInPoints] = useState<{ [key: string]: string }>({});
  const [voOutPoints, setVoOutPoints] = useState<{ [key: string]: string }>({});
  const [songTitles, setSongTitles] = useState<{ [key: string]: string }>({});
  const [songArtists, setSongArtists] = useState<{ [key: string]: string }>({});
  const [processedAudioUrl, setProcessedAudioUrl] = useState<string | null>(null);
  const [lineAudioFiles, setLineAudioFiles] = useState<{ [key: string]: string }>({});
  const [lineGains, setLineGains] = useState<{ [key: string]: number }>({});
  // Now using weekly sheet directly instead of render sheet
  const [sheetDate, setSheetDate] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  // Debug tease dialog state
  const [debugDialogOpen, setDebugDialogOpen] = useState(false);
  const [debugTeaseUrls, setDebugTeaseUrls] = useState<{horizontal: string, vertical: string}>({horizontal: '', vertical: ''});

  // Debug function to show tease URLs only
  const handleDebugTease = async () => {
    console.log(`[DEBUG TEASE] Debug button clicked`);

    try {
      // Fetch settings data from the chart-data DB to get tease URLs
      console.log(`[DEBUG TEASE] Fetching settings data from sheet: ${activeSpreadsheetId}`);
      const { settings } = await chartDataService.getSettings(activeSpreadsheetId);

      // Tease movie URLs are language-scoped (EN keeps the unsuffixed keys)
      const debugLangSuffix = activeLanguage === 'en' ? '' : `_${activeLanguage}`;
      const debugHorizontalTeaseUrl = settings[`tease_horizontal${debugLangSuffix}`] || '';
      const debugVerticalTeaseUrl = settings[`tease_vertical${debugLangSuffix}`] || '';

      console.log(`[DEBUG TEASE] Horizontal: ${debugHorizontalTeaseUrl}`);
      console.log(`[DEBUG TEASE] Vertical: ${debugVerticalTeaseUrl}`);

      // Show debug dialog
      setDebugTeaseUrls({
        horizontal: debugHorizontalTeaseUrl || '',
        vertical: debugVerticalTeaseUrl || ''
      });
      setDebugDialogOpen(true);
    } catch (error) {
      console.error(`[DEBUG TEASE] Error fetching settings:`, error);
      setDebugTeaseUrls({
        horizontal: 'Error fetching data',
        vertical: 'Error fetching data'
      });
      setDebugDialogOpen(true);
    }
  };

  // Social clip column indices/data
  const [s3UrlColIndex, setS3UrlColIndex] = useState<number | null>(null);
  const [inPointColIndex, setInPointColIndex] = useState<number | null>(null);
  const [durationColIndex, setDurationColIndex] = useState<number | null>(null);
  const [positionColIndex, setPositionColIndex] = useState<number | null>(null);
  const [s3Urls, setS3Urls] = useState<{ [key: string]: string }>({});
  const [videoInPoints, setVideoInPoints] = useState<{ [key: string]: string }>({});
  const [videoDurations, setVideoDurations] = useState<{ [key: string]: string }>({});
  const [chartPositions, setChartPositions] = useState<{ [key: string]: string }>({});
  const [globalGain, setGlobalGain] = useState<number>(0); // Global gain from P14

  // State for final video assembly
  const [isAssemblingVideo, setIsAssemblingVideo] = useState<boolean>(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [assemblyError, setAssemblyError] = useState<string | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [assemblySuccessMessage, setAssemblySuccessMessage] = useState<string | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  
  // State for movie rendering jobs
  const [movieProjects, setMovieProjects] = useState<{id: string, format: string, status: string, url?: string, teaseUrl?: string}[]>([]);
  const [isPollingMovieStatus, setIsPollingMovieStatus] = useState<boolean>(false);
  const [activePlayerTab, setActivePlayerTab] = useState(0);
  const [recentOutputs, setRecentOutputs] = useState<any[]>([]); // Added for recent outputs list
  const [baseFileNameForExport, setBaseFileNameForExport] = useState<string>(''); // Added for base filename
  const [selectedRecentVideoUrl, setSelectedRecentVideoUrl] = useState<string | null>(null); // Added for playing recent render
  const [locallyViewedUrls, setLocallyViewedUrls] = useState<Set<string>>(new Set()); // Track viewed outputs for dot dismissal
  const [renderProgress, setRenderProgress] = useState<number>(0);
  const [renderMessage, setRenderMessage] = useState<string>('');
  const [isReadyForExport, setIsReadyForExport] = useState<boolean>(false); // Ensure this is added

  // Sorting state for Output History table
  const [sortBy, setSortBy] = useState<'timestamp' | 'chart' | 'sheetDate' | 'format'>('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Video popup dialog state
  const [videoDialogOpen, setVideoDialogOpen] = useState<boolean>(false);
  const [videoDialogUrl, setVideoDialogUrl] = useState<string>('');
  const [videoDialogTitle, setVideoDialogTitle] = useState<string>('');

  // Export job tracking state
  const [exportJobs, setExportJobs] = useState<any[]>([]);
  const [currentExportJobId, setCurrentExportJobId] = useState<string | null>(null);
  const [isPollingExportJobs, setIsPollingExportJobs] = useState<boolean>(false);

  const isPollingMovieStatusRef = useRef(isPollingMovieStatus);
  useEffect(() => {
    isPollingMovieStatusRef.current = isPollingMovieStatus;
  }, [isPollingMovieStatus]);

  const movieProjectsRef = useRef(movieProjects);
  useEffect(() => {
    movieProjectsRef.current = movieProjects;
  }, [movieProjects]);

  // Ref to access currentExportJobId in polling callback without stale closure
  const currentExportJobIdRef = useRef(currentExportJobId);
  useEffect(() => {
    currentExportJobIdRef.current = currentExportJobId;
  }, [currentExportJobId]);

  // Dispatch event when export polling state changes (for SpreadsheetViewer badge polling)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('exportPollingChange', {
      detail: { isPolling: isPollingExportJobs }
    }));
  }, [isPollingExportJobs]);

  // Ref to track which completed job IDs we've already processed for UI refresh
  const processedCompletedJobsRef = useRef<Set<string>>(new Set());

  // Fetch VO data function
  const fetchVOData = useCallback(async (): Promise<void> => {
    if (!activeSpreadsheetId) {
      setIsReadyForExport(false); // Not ready if no prop ID
      return;
    }
    setLoading(true);
    setError(null);
    setIsReadyForExport(false); // Set to false at the beginning of fetching

    // Ensure activeSpreadsheetId state is processed before proceeding
    // Note: activeSpreadsheetId is set by a separate useEffect.
    // We need to ensure this fetchVOData either runs after activeSpreadsheetId is set,
    // or that activeSpreadsheetId is passed to it or read directly if stable.
    // For now, we rely on the guard in handleExport for activeSpreadsheetId.

    try {
      console.log(`[OutputManager] Loading VO data from chart-data DB: ${activeSpreadsheetId}`);

      // Typed VO maps (keyed '10'..'1' | 'Intro' | 'Outro' | 'Tease'). The server
      // already resolves the per-line (T/U) over base (N/O) timing fallback and the
      // line-gain parsing, so these maps are used as-is — no header discovery or A1
      // ranges. `activeLanguage` selects the bilingual VO; positions/settings are shared.
      const vo = await chartDataService.getVO(activeSpreadsheetId, activeLanguage);

      // Social-clip fields (S3 URL / IN Point / Duration / Position) come from the
      // typed positions, keyed by position string ('10'..'1') to mirror the prior shape.
      const positions = await chartDataService.getPositions(activeSpreadsheetId);

      const newVoInPoints: { [key: string]: string } = { ...(vo.voInPoints || {}) };
      const newVoOutPoints: { [key: string]: string } = { ...(vo.voOutPoints || {}) };
      const newSongTitles: { [key: string]: string } = { ...(vo.songTitles || {}) };
      const newSongArtists: { [key: string]: string } = { ...(vo.songArtists || {}) };
      const newLineAudioFiles: { [key: string]: string } = { ...(vo.lineVoUrls || {}) }; // Line VO URLs (col S)
      const newLineGains: { [key: string]: number } = { ...(vo.lineGain || {}) }; // Line gains (col P)
      // --- Start: Ensure social clip data objects are initialized ---
      const newS3Urls: { [key: string]: string } = {};
      const newVideoInPoints: { [key: string]: string } = {};
      const newVideoDurations: { [key: string]: string } = {};
      const newChartPositions: { [key: string]: string } = {};
      // --- End: Ensure social clip data objects are initialized ---

      for (const p of positions) {
        const key = String(p.position); // '10'..'1'
        newS3Urls[key] = p.s3Url || '';
        newVideoInPoints[key] = p.inPoint || '';
        newVideoDurations[key] = p.duration || '';
        newChartPositions[key] = String(p.position);
      }

      const extractedDate = vo.dateString || null;

      setSheetDate(extractedDate);
      console.log(`[OutputManager] Sheet date: ${extractedDate}`);

      setVoInPoints(newVoInPoints);
      setVoOutPoints(newVoOutPoints);
      setSongTitles(newSongTitles);
      setSongArtists(newSongArtists);
      setLineAudioFiles(newLineAudioFiles); // Set line audio files (col S)
      console.log(`[OutputManager] Loaded ${Object.keys(newLineAudioFiles).length} line audio files (col S):`, newLineAudioFiles);
      setLineGains(newLineGains); // Set line gains (col P)
      console.log(`[OutputManager] Loaded ${Object.keys(newLineGains).length} line-specific gains (col P):`, newLineGains);
      // --- Start: Set state for social clip data ---
      setS3Urls(newS3Urls);
      setVideoInPoints(newVideoInPoints);
      setVideoDurations(newVideoDurations);
      setChartPositions(newChartPositions);
      // --- End: Set state for social clip data ---

      // Processed (monolithic) audio URL — now from vo.processedVoUrl (was the M14 cell).
      // Store the raw value; the export payload builder prefixes non-http URLs with
      // the server base, so don't prefix here (preserves prior behavior).
      if (vo.processedVoUrl && vo.processedVoUrl.trim()) {
        console.log('[OutputManager] Loaded processed audio URL from DB:', vo.processedVoUrl);
        setProcessedAudioUrl(vo.processedVoUrl.trim());
      } else {
        console.log('[OutputManager] No processed audio URL found in DB');
        setProcessedAudioUrl(null);
      }

      // Global gain — now from vo.globalGain (was the P14 cell).
      if (vo.globalGain != null && String(vo.globalGain).trim() !== '' && !isNaN(parseFloat(String(vo.globalGain)))) {
        const gainValue = parseFloat(String(vo.globalGain));
        console.log('[OutputManager] Loaded global gain from DB:', gainValue);
        setGlobalGain(gainValue);
        // Also save to localStorage for VOManager synchronization
        localStorage.setItem(`globalGain_${activeSpreadsheetId}`, String(gainValue));
      } else {
        console.log('[OutputManager] No valid global gain found in DB');
        setGlobalGain(0);
      }

      // Check if all required data is set before declaring ready
      if (activeSpreadsheetId && extractedDate) {
        setIsReadyForExport(true);
        console.log('[OutputManager] fetchVOData successful: isReadyForExport set to true.', { activeSpreadsheetId, extractedDate });
      } else {
        setIsReadyForExport(false);
        let missing = [];
        if (!activeSpreadsheetId) missing.push("weeklySheetId");
        if (!extractedDate) missing.push("sheetDate");
        console.warn(`[OutputManager] fetchVOData completed but not ready. Missing: ${missing.join(', ')}`);
        setError(`Initialization incomplete. Missing: ${missing.join(', ')}.`);
      }

    } catch (err: any) {
      console.error('[OutputManager] Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load required VO data');
      if (err?.message && err.message.includes('403')) {
          setError('Permission error fetching chart data. Please check access rights.');
      }
      setIsReadyForExport(false); // Ensure false on error
    } finally {
      setLoading(false);
    }
  }, [activeSpreadsheetId, activeLanguage]);

  // Bilingual: adopt the active sheet's stored VO language. fetchVOData reads this
  // language directly (via the typed getVO call), so changing it re-runs the load
  // through the [activeSpreadsheetId, fetchVOData] effect below — no separate sync.
  useEffect(() => {
    setActiveLanguageState(getVoLanguage(activeSpreadsheetId));
  }, [activeSpreadsheetId]);

  const handleSetLanguage = useCallback((lang: VoLanguage) => {
    if (activeSpreadsheetId) setVoLanguage(activeSpreadsheetId, lang);
    setActiveLanguageState(lang);
    // Changing activeLanguage gives fetchVOData a new identity, so the
    // [activeSpreadsheetId, fetchVOData] effect re-reads VO for this language
    // (in/out points, line audio, gains) — no explicit fetch needed here.
  }, [activeSpreadsheetId]);

  useEffect(() => {
    if (activeSpreadsheetId) {
      console.log('[OutputManager] activeSpreadsheetId available, calling fetchVOData');
      fetchVOData();
    } else {
      console.log('[OutputManager] No activeSpreadsheetId, clearing all state');
      setVoInPoints({});
      setVoOutPoints({});
      setSongTitles({});
      setSongArtists({});
      setProcessedAudioUrl(null);
      setLineAudioFiles({});
      setError(null);
      setLoading(false);
      // Now using weekly sheet directly instead of render sheet
      // --- Start: Clear social clip state if no spreadsheetId ---
      setS3UrlColIndex(null);
      setInPointColIndex(null);
      setDurationColIndex(null);
      setPositionColIndex(null);
      setS3Urls({});
      setVideoInPoints({});
      setVideoDurations({});
      setChartPositions({});
      // --- End: Clear social clip state if no spreadsheetId ---
      setIsReadyForExport(false); // Also set to false if no propId
    }
  }, [activeSpreadsheetId, fetchVOData]);
  
  // Basic useEffect to simulate loading
  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  const handleCancelGeneration = useCallback(() => {
    console.log('[OutputManager] Cancelling generation process.');
    setIsExporting(false);

    enqueueSnackbar('Generation process cancelled by user.', { variant: 'warning' });
  }, [enqueueSnackbar]);

  const handleExport = async (): Promise<void> => {
    console.log('[OutputManager Export] === STARTING EXPORT (Background Processing) ===');
    console.log('[OutputManager Export] activeSpreadsheetId:', activeSpreadsheetId);
    console.log('[OutputManager Export] sheetDate:', sheetDate);
    console.log('[OutputManager Export] outputFormat:', outputFormat);

    // CRITICAL GUARD CLAUSE
    if (!isReadyForExport || !activeSpreadsheetId || !sheetDate) {
      enqueueSnackbar('Component is not ready or critical data is missing. Please wait or refresh.', { variant: 'warning' });
      console.warn('[OutputManager Export] Aborted: Not ready or missing critical data.', {
        isReadyForExport,
        activeSpreadsheetId,
        sheetDate
      });
      setIsExporting(false);
      return;
    }

    if (!outputFormat || outputFormat.length === 0) {
      enqueueSnackbar('Please select at least one output format.', { variant: 'warning' });
      return;
    }

    setValidationErrors([]);
    setIsExporting(true);
    setError(null);
    setMovieProjects([]);
    setIsPollingMovieStatus(false);

    try {
      // Run client-side validation first for fast feedback. Reads come from the
      // chart-data DB via TYPED methods — no spreadsheet grid, no column letters:
      // getPositions() (typed position + linked-video fields), getVO() (per-section
      // VO text/url/in-out + the monolithic processedVoUrl), getSettings() (key→value).
      // A second attempt after a short delay is cheap insurance against a transient
      // read right after an edit is persisted.
      console.log('[OutputManager Export] Running client-side validation...');
      const fetchAndValidate = async (): Promise<ValidationError[]> => {
        const [positions, vo, settingsResult] = await Promise.all([
          chartDataService.getPositions(activeSpreadsheetId),
          chartDataService.getVO(activeSpreadsheetId, activeLanguage),
          chartDataService.getSettings(activeSpreadsheetId),
        ]);
        return validateSheetDataForExport(positions, vo, settingsResult?.settings || {}, outputFormat);
      };

      const MAX_VALIDATION_ATTEMPTS = 2;
      const VALIDATION_RETRY_DELAY_MS = 1500;
      let clientValidationErrors = await fetchAndValidate();
      for (let attempt = 1; clientValidationErrors.length > 0 && attempt < MAX_VALIDATION_ATTEMPTS; attempt++) {
        console.log(`[OutputManager Export] Validation found ${clientValidationErrors.length} error(s); re-reading in ${VALIDATION_RETRY_DELAY_MS}ms to rule out a transient post-edit read (attempt ${attempt + 1}/${MAX_VALIDATION_ATTEMPTS})...`);
        await new Promise(resolve => setTimeout(resolve, VALIDATION_RETRY_DELAY_MS));
        clientValidationErrors = await fetchAndValidate();
      }

      if (clientValidationErrors.length > 0) {
        console.log(`[OutputManager Export] Client-side validation failed with ${clientValidationErrors.length} errors:`);
        clientValidationErrors.forEach((err, idx) => {
          console.log(`  [Validation Error ${idx + 1}] ${err.section || 'General'}${err.position ? ` #${err.position}` : ''}: ${err.field}${err.cell ? ` (${err.cell})` : ''} - ${err.message}`);
        });
        // Fire-and-forget: record the failure server-side so it shows up in
        // Railway logs (client validation otherwise leaves no server trace).
        // Reported AFTER the retry, so this is a genuine persistent failure.
        fetch(`${API_BASE_URL}/api/log-validation-errors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            spreadsheetId: activeSpreadsheetId,
            sheetDate,
            format: outputFormat,
            attempts: MAX_VALIDATION_ATTEMPTS,
            errors: clientValidationErrors,
          }),
        }).catch(() => { /* never let logging affect the export flow */ });
        setValidationErrors(clientValidationErrors);
        setValidationDialogOpen(true);
        setIsExporting(false);
        return;
      }
      console.log('[OutputManager Export] Client-side validation passed');

      // Format the sheet date for file naming
      let formattedSheetDate = 'unknown-date';
      if (sheetDate) {
        try {
          const dateObj = new Date(sheetDate);
          if (!isNaN(dateObj.getTime())) {
            const month = dateObj.getMonth() + 1;
            const day = dateObj.getDate();
            const year = dateObj.getFullYear().toString().slice(-2);
            formattedSheetDate = `${month}-${day}-${year}`;
            console.log(`[OutputManager Export] Formatted sheet date: '${formattedSheetDate}'`);
          }
        } catch (dateError) {
          console.error(`[OutputManager Export] Error parsing sheet date '${sheetDate}':`, dateError);
        }
      }
      const exportVoSubfolder = currentChartConfig?.voSubfolder || '';
      // Bilingual: ES constructed/silent VO URLs live under a /es/ segment (mirrors
      // server buildVoS3Prefix); EN stays segment-less. Per-line uploaded URLs in
      // lineAudioFiles already carry the right path from upload, so this only
      // affects the fallback-constructed URLs below.
      const exportVoLangSeg = (activeLanguage && activeLanguage !== 'en') ? `${activeLanguage}/` : '';
      const exportVoS3Base = `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/VO/${exportVoSubfolder ? exportVoSubfolder + '/' : ''}${exportVoLangSeg}${formattedSheetDate}`;

      // Load audio settings from localStorage
      const audioSettingsStorageKey = `audioExportSettings_${activeSpreadsheetId}`;
      const savedSettingsStr = localStorage.getItem(audioSettingsStorageKey);
      let audioSettingsForPayload: { makeupGain: string; [key: string]: string } = { makeupGain: '0' };

      if (savedSettingsStr) {
        try {
          const parsedSettings = JSON.parse(savedSettingsStr);
          if (parsedSettings.makeupGain !== undefined) {
            audioSettingsForPayload.makeupGain = String(parsedSettings.makeupGain);
          } else if (parsedSettings.gain !== undefined) {
            audioSettingsForPayload.makeupGain = String(parsedSettings.gain);
          }
          // Copy line-specific gains
          Object.keys(parsedSettings).forEach(key => {
            if (key.startsWith('lineGain_')) {
              audioSettingsForPayload[key] = parsedSettings[key];
            }
          });
        } catch (e) {
          console.error(`[OutputManager Export] Failed to parse audio settings:`, e);
        }
      }
      console.log('[OutputManager Export] Audio settings:', audioSettingsForPayload);

      // Prepare VO data from frontend state
      const exportVoData = DISPLAY_ORDER.map(key => {
        const inPointStr = String(voInPoints[key] || '');
        const outPointStr = String(voOutPoints[key] || '');
        let inPoint = parseFloat(inPointStr || '0');
        let outPoint = parseFloat(outPointStr || '0');

        const isSilentAudio = (!inPointStr || inPointStr.trim() === '' || inPointStr === '0') &&
                             (!outPointStr || outPointStr.trim() === '' || outPointStr === '0');

        let audioUrl;
        let isUsingDefaultOutro = false;

        // Priority: per-line audio (column S) > monolithic VO (M14) > silent/constructed URL
        if (lineAudioFiles[key]) {
          // Per-line audio exists (generated or uploaded) — always use it
          audioUrl = lineAudioFiles[key];
          if (isSilentAudio) {
            // No in/out points set — use full audio file (0 to 3s default, will be overridden by actual duration on server)
            inPoint = 0;
            if (key === 'Intro' || key === 'Tease') {
              outPoint = 5;
            } else if (key === 'Outro') {
              outPoint = 7; // Default outro duration
            } else {
              outPoint = 3;
            }
          }
          console.log(`[OutputManager Export] Using line audio for ${key}: ${audioUrl} (in: ${inPoint}, out: ${outPoint})`);
        } else if (isSilentAudio && key === 'Tease') {
          // A silent Tease (no per-line audio, no in/out points) means this chart
          // has no tease this week. Do NOT fabricate ${base}/tease.wav — that file
          // was never generated, so the export download 403s and the whole job
          // fails. Leaving audioUrl unset drops the section via the filter below.
          // (Mirrors how the settings payload gates tease on hasTeaseContent.)
          audioUrl = undefined;
        } else if (isSilentAudio && key !== 'Outro') {
          const filename = key === 'Intro' ? 'intro.wav' :
                          key === 'Tease' ? 'tease.wav' : `${key}.wav`;
          audioUrl = `${exportVoS3Base}/${filename}`;
          inPoint = 0;
          if (key === 'Intro' || key === 'Tease') {
            outPoint = 5;
          } else {
            outPoint = 3;
          }
        } else {
          audioUrl = processedAudioUrl;
          if (isSilentAudio) {
            inPoint = 0;
            if (key === 'Intro' || key === 'Tease') {
              outPoint = 5;
            } else if (key !== 'Outro') {
              outPoint = 3;
            }
          }
        }

        if (key === 'Outro' && !lineAudioFiles[key] && (isNaN(inPoint) || isNaN(outPoint) || (inPoint === 0 && outPoint === 0))) {
          // Render the same S3 default outro the VO Manager preview uses (single
          // source of truth). The export server downloads non-/uploads/ /public/
          // http URLs, and its 0dB default-VO gain rule matches default-outro.wav.
          audioUrl = getDefaultOutroAudioUrl(currentChartConfig?.voSubfolder);
          inPoint = 0;
          outPoint = getDefaultOutroDuration(currentChartConfig?.voSubfolder);
          isUsingDefaultOutro = true;
        }

        // Determine gain
        let gainToApply: number | undefined = undefined;
        if (key === 'Outro' && isUsingDefaultOutro) {
          gainToApply = undefined;
        } else if (lineGains[key] !== undefined) {
          gainToApply = lineGains[key];
        } else if (globalGain !== 0) {
          gainToApply = globalGain;
        }

        return {
          key,
          audioUrl: audioUrl ? (typeof audioUrl === 'string' && audioUrl.startsWith('http') ? audioUrl : `${API_BASE_URL}${audioUrl}`) : null,
          inPoint,
          outPoint,
          songTitle: songTitles[key] || '',
          artist: songArtists[key] || '',
          gain: gainToApply
        };
      }).filter((vo): vo is Exclude<typeof vo, null> => {
        if (!vo || !vo.audioUrl) return false;
        if (isNaN(vo.inPoint) || isNaN(vo.outPoint)) return false;
        if (vo.inPoint === 0 && vo.outPoint === 0) return false;
        return true;
      });

      console.log(`[OutputManager Export] Prepared ${exportVoData.length} VO items`);

      // Send to server for background processing
      // Server will handle: validation, VO export, JSON2Video submission, polling, S3 finalization
      const response = await fetch('/api/export-manager/start-export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weeklySheetId: activeSpreadsheetId,
          sheetDate: sheetDate, // Use original date format for display, backend converts for VO URLs
          requestedFormats: outputFormat,
          audioSettings: audioSettingsForPayload,
          voData: exportVoData,
          chartName: chartCategory,
          horizontalTemplateId: currentChartConfig?.horizontalTemplateId,
          verticalTemplateId: currentChartConfig?.verticalTemplateId,
          voSubfolder: currentChartConfig?.voSubfolder || '',
          language: activeLanguage // bilingual: render this language's VO + tag the output
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        // Check for validation errors
        if (result.validationErrors && result.validationErrors.length > 0) {
          setValidationErrors(result.validationErrors);
          setValidationDialogOpen(true);
          setIsExporting(false);
          return;
        }
        throw new Error(result.error || 'Export failed');
      }

      console.log(`[OutputManager Export] Export job created: ${result.jobId}`);
      enqueueSnackbar('Export started! You can navigate away safely.', { variant: 'success' });

      // Track this as the current export job so we show its videos when it completes
      setCurrentExportJobId(result.jobId);
      currentExportJobIdRef.current = result.jobId;  // Also update ref directly to avoid race condition

      // Add the new job to the export jobs list so the UI shows it immediately
      const newJob = {
        id: result.jobId,
        status: 'pending' as const,
        current_step: 'Starting export...',
        processing_phase: 'queued',
        sheet_date: formattedSheetDate,
        requested_formats: outputFormat,
        created_at: new Date().toISOString(),
        subJobs: result.job?.subJobs || []
      };
      setExportJobs(prev => {
        // Pre-populate processedCompletedJobsRef with existing completed jobs
        // so they don't get treated as "newly completed" when polling starts
        prev.forEach(job => {
          if (job.status === 'completed') {
            processedCompletedJobsRef.current.add(job.id);
          }
        });
        return [...prev, newJob];
      });

      // Start polling for job status updates
      setIsPollingExportJobs(true);

    } catch (err) {
      console.error('[OutputManager Export] Error:', err);
      setError(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
      enqueueSnackbar(`Export failed: ${err instanceof Error ? err.message : String(err)}`, { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };
  
  // Helper function to get the weekly chart's name from its id. Sourced from the
  // chart-data DB (the week's sheet_name, e.g. "6-13-26") — fetchSheetDetails parses
  // the date out of it just as it did with the old Drive spreadsheet title.
  const getSheetName = async (spreadsheetId: string): Promise<string> => {
    try {
      const vo = await chartDataService.getVO(spreadsheetId, activeLanguage);
      return vo?.sheetName || vo?.dateString || `sheet-${Date.now()}`;
    } catch (error) {
      console.error('[OutputManager] Error getting sheet name:', error);
      // Fallback to a timestamp-based name
      return `sheet-${Date.now()}`;
    }
  };
  
  const handleValidationDialogClose = () => {
    setValidationDialogOpen(false);
  };

  // Comprehensive validation function for sheet data before JSON2Video export
  const validateSheetDataForExport = async (
    positions: import('../services/chartDataService').ChartPosition[],
    vo: {
      voData: { [k: string]: string };
      voInPoints: { [k: string]: string };
      voOutPoints: { [k: string]: string };
      lineVoUrls: { [k: string]: string };
      processedVoUrl: string | null;
    },
    settings: { [key: string]: any },
    outputFormats: string[]
  ): Promise<ValidationError[]> => {
    const errors: ValidationError[] = [];

    // True when a value is missing/blank. (No spreadsheet error sentinels — this is
    // typed DB data, not cells; a DB null/'' is the only "empty".)
    const isEmpty = (val: any): boolean => val === undefined || val === null || String(val).trim() === '';

    // Read a settings key (settings is a typed key→value object from getSettings()).
    const setting = (key: string): string => {
      const v = settings[key];
      return v === undefined || v === null ? '' : String(v).trim();
    };
    // VO section text/url by section key ('1'..'10' | 'Intro' | 'Outro' | 'Tease').
    const voText = (key: string): string => (vo.voData[key] ?? '').trim();

    // Any VO script anywhere → run VO validation (else allow a music-only export).
    const VO_SECTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'Intro', 'Outro', 'Tease'];
    const hasAnyVoText = VO_SECTIONS.some(k => !isEmpty(voText(k)));

    // Monolithic-VO workflow: the VO is one processed file (vo.processedVoUrl), and the
    // per-position clips are CUT from it at export time — there is no per-line VO URL in
    // this mode. So when a monolithic processed VO exists, do NOT require a per-line VO
    // URL. VO Text + VO In/Out are still validated (the monolithic trim needs them).
    // Per-line files and the monolithic VO can COEXIST: one re-recorded line uploaded
    // individually overrides just that line, and the export falls back to the monolithic
    // file for every other line (see the audio-source priority in the export payload).
    // Do NOT require zero per-line files here — that made a single line override flag
    // every other line as "Missing Voice Over URL" and blocked the export.
    const hasMonolithicVo = !isEmpty(vo.processedVoUrl);

    console.log(`[Validation] hasAnyVoText: ${hasAnyVoText} - ${hasAnyVoText ? 'validating VO fields' : 'skipping VO validation'}; hasMonolithicVo: ${hasMonolithicVo}${hasMonolithicVo ? ' (per-line VO URL not required)' : ''}`);

    // ---- Chart positions (1..10) ----
    const byPosition = new Map<number, import('../services/chartDataService').ChartPosition>();
    positions.forEach(p => byPosition.set(Number(p.position), p));

    for (let position = 1; position <= 10; position++) {
      const p = byPosition.get(position);
      const sectionKey = String(position); // VO is keyed by position string
      // Per-line override VO present for this position (URL + in + out all set).
      const hasLineVo = !isEmpty(vo.lineVoUrls[sectionKey]) &&
                        !isEmpty(vo.voInPoints[sectionKey]) &&
                        !isEmpty(vo.voOutPoints[sectionKey]);

      const pushIfEmpty = (val: any, name: string) => {
        if (isEmpty(val)) {
          errors.push({ field: name, message: `Missing ${name} for Song ${position}`, section: 'chart', position });
        }
      };

      pushIfEmpty(p?.song, 'Song');
      pushIfEmpty(p?.artist, 'Artist');
      pushIfEmpty(p?.lastWeek, 'Last Week');
      pushIfEmpty(p?.peak, 'Peak');
      pushIfEmpty(p?.weeks, 'Weeks');
      pushIfEmpty(p?.s3Url, 'S3 URL');
      pushIfEmpty(p?.inPoint, 'IN Point');
      pushIfEmpty(p?.duration, 'Duration');

      if (hasAnyVoText) {
        pushIfEmpty(voText(sectionKey), 'Voice Over Text');
        // Per-line VO URL not required in monolithic mode (cut from the processed file).
        if (!hasMonolithicVo) pushIfEmpty(vo.lineVoUrls[sectionKey], 'Voice Over URL');
        // VO In/Out only required when there is no line-override VO for this position.
        if (!hasLineVo) {
          pushIfEmpty(vo.voInPoints[sectionKey], 'VO In');
          pushIfEmpty(vo.voOutPoints[sectionKey], 'VO Out');
        }
      }

      if (outputFormats.includes('vertical')) {
        pushIfEmpty(p?.verticalUrl, 'Vertical URL');
      }

      // JSON2Video fails with 0 duration.
      const durationVal = parseFloat(String(p?.duration ?? ''));
      if (!isNaN(durationVal) && durationVal === 0) {
        errors.push({ field: 'Duration', message: `Duration cannot be 0 for Song ${position}`, section: 'chart', position });
      }
    }

    // ---- Intro VO (text + per-line URL, unless monolithic) ----
    if (hasAnyVoText) {
      if (isEmpty(voText('Intro'))) {
        errors.push({ field: 'Intro Voice Over Text', message: 'Missing Intro Voice Over Text', section: 'intro' });
      }
      if (!hasMonolithicVo && isEmpty(vo.lineVoUrls['Intro'])) {
        errors.push({ field: 'Intro Voice Over URL', message: 'Missing Intro Voice Over URL', section: 'intro' });
      }
    }

    // ---- Intro settings (by key; intro_voiceover_url is generated at export) ----
    const introSettings: { key: string; isUrl?: boolean }[] = [
      { key: 'carousel_video1_url', isUrl: true }, { key: 'carousel_video1_seek' },
      { key: 'carousel_video2_url', isUrl: true }, { key: 'carousel_video2_seek' },
      { key: 'carousel_video3_url', isUrl: true }, { key: 'carousel_video3_seek' },
      { key: 'carousel_video4_url', isUrl: true }, { key: 'carousel_video4_seek' },
      { key: 'video_left_url', isUrl: true }, { key: 'video_left_seek' },
      { key: 'date_string' },
      ...(hasAnyVoText ? [
        { key: 'intro_volume_during_voiceover' },
        { key: 'intro_volume_after_voiceover' },
        { key: 'intro_voiceover_volume' },
      ] : []),
    ];
    for (const s of introSettings) {
      if (isEmpty(setting(s.key))) {
        errors.push({ field: s.key, message: `Missing ${s.key.replace(/_/g, ' ')}`, section: 'intro' });
      }
    }

    // ---- Outro settings (by key; outro_voiceover_url is generated at export) ----
    const outroSettings: { key: string; isUrl?: boolean }[] = [
      { key: 'outro_video1_url', isUrl: true }, { key: 'outro_video1_seek' },
      { key: 'outro_video2_url', isUrl: true }, { key: 'outro_video2_seek' },
      { key: 'outro_video3_url', isUrl: true }, { key: 'outro_video3_seek' },
      { key: 'outro_video4_url', isUrl: true }, { key: 'outro_video4_seek' },
      { key: 'outro_video5_url', isUrl: true }, { key: 'outro_video5_seek' },
      { key: 'outro_video6_url', isUrl: true }, { key: 'outro_video6_seek' },
      ...(hasAnyVoText ? [
        { key: 'outro_volume_during_voiceover' },
        { key: 'outro_volume_after_voiceover' },
        { key: 'outro_voiceover_volume' },
        { key: 'videoclip_volume_during_voiceover' },
        { key: 'videoclip_volume_after_voiceover' },
        { key: 'videoclip_voiceover_volume' },
      ] : []),
      { key: 'videoclip_volume_final_number1' },
      { key: 'videoclip_volume_final_other' },
      { key: 'in_point_adjust' },
    ];
    for (const s of outroSettings) {
      if (isEmpty(setting(s.key))) {
        errors.push({ field: s.key, message: `Missing ${s.key.replace(/_/g, ' ')}`, section: 'outro' });
      }
    }

    // ---- Tease settings (only when tease VO content exists) ----
    const hasTeaseContent = !isEmpty(voText('Tease'));
    const teaseSettings: { key: string; isUrl?: boolean }[] = [
      { key: 'tease_shot1_url', isUrl: true },
      { key: 'tease_shot1_seek' },
      { key: 'tease_shot1_duration' },
    ];
    if (hasTeaseContent) {
      for (const s of teaseSettings) {
        if (isEmpty(setting(s.key))) {
          errors.push({ field: s.key, message: `Missing ${s.key.replace(/_/g, ' ')} (required when tease content exists)`, section: 'tease' });
        }
      }
    }

    // ---- File-existence check for URLs we expect to already exist ----
    // (VO URLs are excluded — they're generated during export, so checking them would
    // make a first export impossible.)
    const urlsToCheck: { url: string; field: string; section: ValidationError['section']; position?: number }[] = [];

    for (let position = 1; position <= 10; position++) {
      const p = byPosition.get(position);
      if (p && !isEmpty(p.s3Url)) {
        urlsToCheck.push({ url: String(p.s3Url).trim(), field: 'S3 URL', section: 'chart', position });
      }
      if (outputFormats.includes('vertical') && p && !isEmpty(p.verticalUrl)) {
        urlsToCheck.push({ url: String(p.verticalUrl).trim(), field: 'Vertical URL', section: 'chart', position });
      }
    }

    const urlSettings = [
      ...introSettings.filter(s => s.isUrl),
      ...outroSettings.filter(s => s.isUrl),
      ...(hasTeaseContent ? teaseSettings.filter(s => s.isUrl) : []),
    ];
    for (const s of urlSettings) {
      const val = setting(s.key);
      if (!isEmpty(val)) {
        const section: ValidationError['section'] =
          s.key.startsWith('intro') || s.key.startsWith('carousel') || s.key.startsWith('video_left')
            ? 'intro'
            : s.key.startsWith('outro')
              ? 'outro'
              : 'tease';
        urlsToCheck.push({ url: val, field: s.key, section });
      }
    }

    // Perform batch URL existence check
    if (urlsToCheck.length > 0) {
      try {
        console.log(`[Validation] Checking ${urlsToCheck.length} URLs for file existence...`);
        const response = await fetch(`${API_BASE_URL}/api/check-urls-exist`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ urls: urlsToCheck.map(u => u.url) }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.results) {
            data.results.forEach((result: { url: string; exists: boolean; error?: string }, index: number) => {
              if (!result.exists) {
                const urlInfo = urlsToCheck[index];
                errors.push({
                  field: urlInfo.field,
                  message: `File not found at URL: ${result.error || 'File does not exist'}`,
                  section: urlInfo.section,
                  position: urlInfo.position,
                  isMissingFile: true,
                });
              }
            });
          }
          console.log(`[Validation] URL check complete: ${data.summary?.existing || 0} exist, ${data.summary?.missing || 0} missing`);
        } else {
          console.warn('[Validation] URL check endpoint returned error, skipping file existence validation');
        }
      } catch (urlCheckError) {
        console.warn('[Validation] Failed to check URL existence:', urlCheckError);
        // Don't fail validation if URL check fails, just skip it
      }
    }

    return errors;
  };

  const handleFormatChange = (
    event: React.MouseEvent<HTMLElement>,
    newFormats: string[],
  ) => {
    if (newFormats.length > 0) {
      setOutputFormat(newFormats);
    } else {
      if (outputFormat.length === 1) {
        if (outputFormat[0] === 'horizontal') {
          setOutputFormat(['vertical']);
        } 
        else if (outputFormat[0] === 'vertical') {
          setOutputFormat(['horizontal']);
        }
      } 
    }
  };

  // Function to poll json2video for rendering status
  const pollMovieRenderStatus = useCallback(() => {
    console.log('[OutputManager Movie Polling] pollMovieRenderStatus invoked');
    
    let currentTimerId: NodeJS.Timeout | null = null;

    const checkStatus = async () => {
      if (!isPollingMovieStatusRef.current) {
        console.log('[OutputManager Movie Polling] checkStatus: Polling REF is false. Stopping.');
        if (currentTimerId) clearTimeout(currentTimerId);
        return;
      }

      const currentProjects = movieProjectsRef.current; // Use ref to get latest projects
      
      if (currentProjects.length === 0) {
        console.log('[OutputManager Movie Polling] checkStatus: No projects to poll. Setting isPollingMovieStatus to false.');
        setIsPollingMovieStatus(false); // No projects, so ensure polling stops
        if (currentTimerId) clearTimeout(currentTimerId);
        return;
      }
      
      const projectsToPoll = currentProjects.filter(p => p.status !== 'done' && p.status !== 'error' && p.status !== 'finalized_s3'); // Added 'finalized_s3'

      if (projectsToPoll.length === 0) {
        console.log('[OutputManager Movie Polling] checkStatus: All projects are in terminal state. Setting isPollingMovieStatus to false.');
        setIsPollingMovieStatus(false); // All done, so ensure polling stops
        if (currentTimerId) clearTimeout(currentTimerId);
        return;
      }
      
      console.log(`[OutputManager Movie Polling] checkStatus: Polling ${projectsToPoll.length} projects.`);

      const updatedProjectsPromises = currentProjects.map(async (project) => {
        if (project.status === 'done' || project.status === 'error' || project.status === 'finalized_s3') {
          return project;
        }
        
        try {
          const response = await fetch(`/api/json2video/movies?project=${project.id}`);
          
          if (!response.ok) {
            console.error(`[OutputManager Movie Polling] Server error for project ${project.id}: ${response.status}`);
            // Potentially update project status to an API error state
            return {...project, status: 'api_error'}; 
          }
          
          const responseData = await response.json();
          
          if (responseData.success && responseData.movie) {
            const newStatus = responseData.movie.status;
            const prevStatus = project.status;
            
            if (newStatus !== prevStatus) {
              console.log(`[OutputManager Movie Polling] Project ${project.id} old status: ${prevStatus}, new status: ${newStatus}`);
            }
            
            const updatedProjectData = {
              ...project,
              status: newStatus,
              url: responseData.movie.url || project.url // Keep old URL if new one is missing
            };

            console.log(`[DEBUG TEASE URL] Project ${project.id} polling update - teaseUrl preserved: ${updatedProjectData.teaseUrl}`);
            
            if (newStatus === 'done' && prevStatus !== 'done') {
              console.log(`[OutputManager Movie Polling] Project ${project.id} (${project.format}) completed by json2video. URL: ${responseData.movie.url}`);
              enqueueSnackbar(`${project.format} video rendering complete by json2video. Finalizing to S3...`, { variant: 'info' });

              // Call backend to download from json2video URL and upload to S3
              try {
                const finalizeRequestBody = {
                  videoUrl: responseData.movie.url,
                  s3FolderName: 'Billboard/Outputs',
                  baseFileName: baseFileNameForExport || project.id, // Use baseFileName or project.id as fallback
                  format: project.format, // "Horizontal" or "Vertical"
                  sheetDate: sheetDate,
                  teaseUrl: project.teaseUrl || null  // Include tease URL if available
                };

                console.log(`[DEBUG TEASE URL] Finalize-video API call for project ${project.id} - Request body:`, finalizeRequestBody);
                enqueueSnackbar(`🚀 CALLING FINALIZE-VIDEO for ${project.format}: teaseUrl="${finalizeRequestBody.teaseUrl || 'NONE'}"`, { variant: 'info' });

                const finalizeResponse = await fetch(`${API_BASE_URL}/api/finalize-video`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(finalizeRequestBody)
                });

                if (!finalizeResponse.ok) {
                  const errorBody = await finalizeResponse.text();
                  throw new Error(`Failed to finalize video to S3: ${finalizeResponse.status} ${errorBody}`);
                }
                const finalizeResult = await finalizeResponse.json();
                if (finalizeResult.success && finalizeResult.s3Url) {
                  console.log(`[OutputManager Movie Polling] Project ${project.id} (${project.format}) finalized to S3. New URL: ${finalizeResult.s3Url}`);
                  enqueueSnackbar(`${project.format} video successfully saved to S3!`, { variant: 'success' });

                  // Refresh outputs from API (reads from Google Sheets + PostgreSQL)
                  try {
                    const outputsResponse = await fetch(`${API_BASE_URL}/api/get-recent-outputs`);
                    if (outputsResponse.ok) {
                      const outputsData = await outputsResponse.json();
                      setRecentOutputs(outputsData || []);
                      console.log(`[OutputManager Movie Polling] Refreshed recent outputs: ${outputsData?.length || 0} items`);
                    }
                  } catch (fetchError) {
                    console.warn('[OutputManager Movie Polling] Failed to refresh recent outputs:', fetchError);
                  }

                  // Update export job as completed - find the job by looking for matching sub-job
                  try {
                    // Fetch all active export jobs to find the one with this project ID
                    const allJobsResponse = await fetch(`${API_BASE_URL}/api/export-manager/jobs?status=rendering`);
                    if (allJobsResponse.ok) {
                      const allJobsData = await allJobsResponse.json();
                      if (allJobsData.success && allJobsData.jobs) {
                        // Find the job that has a sub-job with this project's external_job_id
                        for (const exportJob of allJobsData.jobs) {
                          if (exportJob.subJobs) {
                            const matchingSubJob = exportJob.subJobs.find((sj: any) =>
                              sj.external_job_id === project.id
                            );

                            if (matchingSubJob) {
                              // Mark sub-job as completed
                              await fetch(`${API_BASE_URL}/api/export-manager/sub-jobs/${matchingSubJob.id}/complete`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  outputUrl: finalizeResult.s3Url,
                                  teaseUrl: project.teaseUrl || null
                                })
                              });
                              console.log(`[OutputManager Movie Polling] Marked sub-job ${matchingSubJob.id} as completed`);

                              // Since each export job now has only one format, mark the whole job as completed
                              await fetch(`${API_BASE_URL}/api/export-manager/jobs/${exportJob.id}/complete`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  [`${project.format.toLowerCase()}Url`]: finalizeResult.s3Url,
                                  [`${project.format.toLowerCase()}TeaseUrl`]: project.teaseUrl || null
                                })
                              });
                              console.log(`[OutputManager Movie Polling] Marked export job ${exportJob.id} as completed`);

                              // Immediately update local state so table reflects completion
                              setExportJobs(prev => prev.map(job =>
                                job.id === exportJob.id
                                  ? { ...job, status: 'completed' }
                                  : job
                              ));

                              break; // Found and processed the matching job
                            }
                          }
                        }
                      }
                    }
                  } catch (exportJobError) {
                    console.error('[OutputManager Movie Polling] Error updating export job:', exportJobError);
                    // Don't fail the video finalization if export job update fails
                  }

                  return { ...updatedProjectData, status: 'finalized_s3', url: finalizeResult.s3Url };
                } else {
                  throw new Error(finalizeResult.error || 'S3 finalization failed with no specific error.');
                }
              } catch (finalizeError: any) {
                console.error(`[OutputManager Movie Polling] Error finalizing project ${project.id} (${project.format}) to S3:`, finalizeError);
                enqueueSnackbar(`Error saving ${project.format} video to S3: ${finalizeError.message}`, { variant: 'error' });

                // Mark export job as failed - find by matching sub-job
                try {
                  const allJobsResponse = await fetch(`${API_BASE_URL}/api/export-manager/jobs?status=rendering`);
                  if (allJobsResponse.ok) {
                    const allJobsData = await allJobsResponse.json();
                    if (allJobsData.success && allJobsData.jobs) {
                      for (const exportJob of allJobsData.jobs) {
                        if (exportJob.subJobs) {
                          const matchingSubJob = exportJob.subJobs.find((sj: any) =>
                            sj.external_job_id === project.id
                          );
                          if (matchingSubJob) {
                            await fetch(`${API_BASE_URL}/api/export-manager/jobs/${exportJob.id}/fail`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ errorMessage: `S3 finalization failed: ${finalizeError.message}` })
                            });
                            break;
                          }
                        }
                      }
                    }
                  }
                } catch (err) {
                  console.error('[OutputManager Movie Polling] Error marking export job as failed:', err);
                }

                return { ...updatedProjectData, status: 'error' }; // Keep json2video URL, mark as error
              }
            } else if (newStatus === 'error' && prevStatus !== 'error') {
              // Handle message being either a string or an object
              let movieErrorMessage = 'Unknown error from json2video';
              if (responseData.movie.message) {
                if (typeof responseData.movie.message === 'string') {
                  movieErrorMessage = responseData.movie.message;
                } else {
                  // If message is an object, stringify it for logging
                  movieErrorMessage = JSON.stringify(responseData.movie.message);
                }
              }
              console.error(`[OutputManager Movie Polling] Project ${project.id} (${project.format}) FAILED (json2video). Message: ${movieErrorMessage}`, responseData.movie);
              enqueueSnackbar(`${project.format} video rendering failed (json2video): ${movieErrorMessage}`, { variant: 'error' });

              // Mark export job as failed - find by matching sub-job
              try {
                const allJobsResponse = await fetch(`${API_BASE_URL}/api/export-manager/jobs?status=rendering`);
                if (allJobsResponse.ok) {
                  const allJobsData = await allJobsResponse.json();
                  if (allJobsData.success && allJobsData.jobs) {
                    for (const exportJob of allJobsData.jobs) {
                      if (exportJob.subJobs) {
                        const matchingSubJob = exportJob.subJobs.find((sj: any) =>
                          sj.external_job_id === project.id
                        );
                        if (matchingSubJob) {
                          await fetch(`${API_BASE_URL}/api/export-manager/jobs/${exportJob.id}/fail`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ errorMessage: movieErrorMessage })
                          });
                          break;
                        }
                      }
                    }
                  }
                }
              } catch (err) {
                console.error('[OutputManager Movie Polling] Error marking export job as failed:', err);
              }
            } else if (newStatus === 'rendering' && prevStatus !== 'rendering') {
              console.log(`[OutputManager Movie Polling] Project ${project.id} now rendering.`);
            }
            return updatedProjectData;
          }
          return project; // Return original project if responseData or movie is not as expected
        } catch (error) {
          console.error(`[OutputManager Movie Polling] Error checking project ${project.id}:`, error);
          // Potentially update project status to a network error state
          return {...project, status: 'network_error'}; 
        }
      });
      
      const newUpdatedProjects = await Promise.all(updatedProjectsPromises);
      setMovieProjects(newUpdatedProjects); 

      const anyProjectStillActiveAfterUpdate = newUpdatedProjects.some(p => p.status !== 'finalized_s3' && p.status !== 'error' && p.status !== 'api_error' && p.status !== 'network_error');
      
      if (isPollingMovieStatusRef.current && anyProjectStillActiveAfterUpdate) {
        currentTimerId = setTimeout(checkStatus, 10000); // Poll every 10 seconds
      } else {
        console.log('[OutputManager Movie Polling] All projects terminal or polling stopped. Halting movie polling.');
        setIsPollingMovieStatus(false);
        if (currentTimerId) clearTimeout(currentTimerId);
        // Export jobs are marked as completed/failed individually when each video is finalized
      }
    };

    // Initial call to start polling if active projects exist
    if (isPollingMovieStatusRef.current && movieProjectsRef.current.some(p => p.status !== 'finalized_s3' && p.status !== 'error')) {
       console.log('[OutputManager Movie Polling] Initial call to checkStatus from pollMovieRenderStatus.');
       currentTimerId = setTimeout(checkStatus, 1000); // Start fairly quickly
    } else {
      console.log('[OutputManager Movie Polling] No active projects to poll initially or polling ref is false.');
    }
    
    return () => {
      if (currentTimerId) {
        console.log('[OutputManager Movie Polling] Cleanup: Clearing movie polling timer.');
        clearTimeout(currentTimerId);
      }
    };
  }, [enqueueSnackbar, sheetDate, baseFileNameForExport]); // Added sheetDate and baseFileNameForExport dependencies

  useEffect(() => {
    let pollCleanupFunction: (() => void) | undefined;

    if (isPollingMovieStatus) {
      console.log('[OutputManager useEffect Polling Trigger] isPollingMovieStatus is true. Calling pollMovieRenderStatus to start/ensure polling.');
      pollCleanupFunction = pollMovieRenderStatus(); // pollMovieRenderStatus now returns its own timer cleanup
    } else {
      // If isPollingMovieStatus becomes false, we rely on the cleanup from the previous run (if any)
      // and the checkStatus internal logic using isPollingMovieStatusRef to stop timeouts.
      console.log('[OutputManager useEffect Polling Trigger] isPollingMovieStatus is false. Polling should stop/be stopped.');
    }
    
    return () => {
        if (pollCleanupFunction) {
            console.log('[OutputManager useEffect Polling Trigger] useEffect cleanup: Calling cleanup from pollMovieRenderStatus.');
            pollCleanupFunction();
        }
    };
  }, [isPollingMovieStatus, pollMovieRenderStatus]); // pollMovieRenderStatus is stable

  // Polling function for export jobs
  const pollExportJobs = useCallback(async () => {
    if (!activeSpreadsheetId) {
      console.log('[Export Jobs Poll] No activeSpreadsheetId, skipping poll');
      return;
    }

    try {
      console.log('[Export Jobs Poll] Fetching export jobs for sheet:', activeSpreadsheetId);
      const response = await fetch(`${API_BASE_URL}/api/export-manager/jobs?weeklySheetId=${activeSpreadsheetId}`);

      if (!response.ok) {
        console.error('[Export Jobs Poll] Failed to fetch jobs:', response.status);
        return;
      }

      const data = await response.json();
      if (data.success && data.jobs) {
        console.log('[Export Jobs Poll] Received jobs:', data.jobs);
        setExportJobs(data.jobs);

        // Check for newly completed jobs that we haven't processed yet
        const newlyCompletedJobs = data.jobs.filter((job: any) =>
          job.status === 'completed' &&
          !processedCompletedJobsRef.current.has(job.id)
        );

        if (newlyCompletedJobs.length > 0) {
          console.log('[Export Jobs Poll] Found newly completed jobs:', newlyCompletedJobs.map((j: any) => j.id));

          // Mark these jobs as processed
          newlyCompletedJobs.forEach((job: any) => {
            processedCompletedJobsRef.current.add(job.id);
          });

          // 1. Refresh recent outputs so the output history updates
          try {
            console.log('[Export Jobs Poll] Refreshing recent outputs...');
            const outputsResponse = await fetch(`${API_BASE_URL}/api/get-recent-outputs`);
            if (outputsResponse.ok) {
              const outputsData = await outputsResponse.json();
              setRecentOutputs(outputsData || []);
              console.log(`[Export Jobs Poll] Refreshed recent outputs: ${outputsData?.length || 0} items`);
            }
          } catch (err) {
            console.warn('[Export Jobs Poll] Failed to refresh outputs:', err);
          }

          // Only show videos in the player if the current export job just completed
          const currentJobId = currentExportJobIdRef.current;
          const currentJobCompleted = currentJobId && newlyCompletedJobs.some((job: any) => job.id === currentJobId);

          if (currentJobCompleted) {
            const currentJob = newlyCompletedJobs.find((job: any) => job.id === currentJobId);
            if (currentJob) {
              const completedProjects: {id: string, format: string, status: string, url?: string, teaseUrl?: string}[] = [];

              if (currentJob.horizontal_url) {
                completedProjects.push({
                  id: `${currentJob.id}-horizontal`,
                  format: 'Horizontal',
                  status: 'finalized_s3',
                  url: currentJob.horizontal_url,
                  teaseUrl: currentJob.horizontal_tease_url || undefined
                });
                console.log(`[Export Jobs Poll] Current job completed - adding horizontal video: ${currentJob.horizontal_url}`);
              }
              if (currentJob.vertical_url) {
                completedProjects.push({
                  id: `${currentJob.id}-vertical`,
                  format: 'Vertical',
                  status: 'finalized_s3',
                  url: currentJob.vertical_url,
                  teaseUrl: currentJob.vertical_tease_url || undefined
                });
                console.log(`[Export Jobs Poll] Current job completed - adding vertical video: ${currentJob.vertical_url}`);
              }

              if (completedProjects.length > 0) {
                setMovieProjects(completedProjects);  // Replace, don't append
                console.log(`[Export Jobs Poll] Showing ${completedProjects.length} videos from current export`);
              }

              // Clear the current job ID since it's done
              setCurrentExportJobId(null);
            }
          }
        }

        // Check if all jobs are completed/failed - stop polling if so
        const activeJobs = data.jobs.filter((job: any) =>
          job.status === 'pending' || job.status === 'rendering'
        );

        if (activeJobs.length === 0 && isPollingExportJobs) {
          console.log('[Export Jobs Poll] No active jobs, stopping poll');
          setIsPollingExportJobs(false);
        }
      }
    } catch (error) {
      console.error('[Export Jobs Poll] Error:', error);
    }
  }, [activeSpreadsheetId, isPollingExportJobs]);

  // Effect to poll export jobs every 10 seconds
  useEffect(() => {
    if (!isPollingExportJobs) {
      return;
    }

    console.log('[Export Jobs Poll] Starting poll interval');

    // Poll immediately
    pollExportJobs();

    // Then poll every 10 seconds
    const interval = setInterval(() => {
      pollExportJobs();
    }, 10000);

    return () => {
      console.log('[Export Jobs Poll] Cleaning up interval');
      clearInterval(interval);
    };
  }, [isPollingExportJobs, pollExportJobs]);

  // Clean up old renders server-side (POST /api/cleanup-old-renders): for chart
  // weeks older than 60 days the server deletes the video of every copy except
  // the newest of each chart × week × orientation × language, and keeps the
  // billing record. (This used to run here in the browser, grouped by chart date
  // alone, and deleted one chart's only copies when two charts shared a week.)
  // Returns the S3 URLs it removed so they can be dropped from the list.
  const cleanupOldOutputs = async (): Promise<string[]> => {
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/cleanup-old-renders`, { method: 'POST' });
      if (!response.ok) return [];
      const { removed = [] } = await response.json();
      if (removed.length) console.log(`[OutputManager] Old-render cleanup removed ${removed.length} older copies`);
      return removed;
    } catch (err) {
      console.warn('[OutputManager] Old-render cleanup failed:', err);
      return [];
    }
  };

  // Effect to fetch recent outputs and export jobs on component mount
  useEffect(() => {
    const fetchRecentOutputs = async () => {
      try {
        console.log('[OutputManager] Fetching recent outputs...');
        const response = await fetch(`${API_BASE_URL}/api/get-recent-outputs`);
        if (!response.ok) {
          throw new Error(`Failed to fetch recent outputs: ${response.status}`);
        }
        const data = await response.json();
        console.log('[OutputManager] Received recent outputs:', data);

        // Remove older copies of renders for chart weeks > 60 days old (server-side).
        const removedUrls = await cleanupOldOutputs();

        // Filter out the removed outputs from the list
        const filteredData = removedUrls.length > 0
          ? (data || []).filter((o: any) => !removedUrls.includes(o.s3Url))
          : (data || []);

        setRecentOutputs(filteredData);
        console.log('[OutputManager] Recent outputs loaded:', filteredData.length);

        // Mark all outputs as viewed and move to Render History
        try {
          const markResponse = await fetch(`${API_BASE_URL}/api/mark-outputs-viewed`, {
            method: 'POST'
          });
          if (markResponse.ok) {
            const markData = await markResponse.json();
            console.log(`[OutputManager] Marked ${markData.movedCount} outputs as viewed`);
          }
        } catch (markError: any) {
          console.error('[OutputManager] Error marking outputs as viewed:', markError);
        }
      } catch (fetchError: any) {
        console.error('[OutputManager] Error fetching recent outputs:', fetchError);
        // Optionally set an error state here or show a snackbar
        // enqueueSnackbar(`Could not load recent renders: ${fetchError.message}`, { variant: 'warning' });
      }
    };

    const fetchExportJobs = async () => {
      if (!activeSpreadsheetId) {
        return;
      }

      try {
        console.log('[OutputManager] Fetching export jobs on mount...');
        const response = await fetch(`${API_BASE_URL}/api/export-manager/jobs?weeklySheetId=${activeSpreadsheetId}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch export jobs: ${response.status}`);
        }
        const data = await response.json();
        console.log('[OutputManager] Raw API response for export jobs:', data);
        if (data.success && data.jobs) {
          setExportJobs(data.jobs);
          console.log('[OutputManager] Export jobs loaded:', data.jobs.length, 'jobs');
          console.log('[OutputManager] Export jobs details:', data.jobs.map((j: any) => ({
            id: j.id?.slice(0,8),
            status: j.status,
            subJobsCount: j.subJobs?.length,
            subJobs: j.subJobs?.map((sj: any) => ({
              id: sj.id?.slice(0,8),
              status: sj.status,
              format: sj.format,
              external_job_id: sj.external_job_id
            }))
          })));

          // Start polling if there are active jobs
          const activeJobs = data.jobs.filter((job: any) =>
            job.status === 'pending' || job.status === 'rendering'
          );
          console.log('[OutputManager] Active jobs found:', activeJobs.length, activeJobs.map((j: any) => ({ id: j.id, status: j.status, subJobs: j.subJobs?.length })));

          if (activeJobs.length > 0) {
            // Pre-populate processedCompletedJobsRef with existing completed jobs
            // so they don't get treated as "newly completed" when polling starts
            data.jobs.forEach((job: any) => {
              if (job.status === 'completed') {
                processedCompletedJobsRef.current.add(job.id);
              }
            });

            setIsPollingExportJobs(true);

            // Resume movie status polling for sub-jobs that have external_job_id
            // This handles the case where user navigated away during rendering
            // Check for both 'rendering' (parent job status) and 'processing' (sub-job status set when submitted to JSON2Video)
            const projectsToResume: {id: string, format: string, status: string}[] = [];
            for (const job of activeJobs) {
              console.log('[OutputManager] Checking job for resume:', job.id, 'subJobs:', job.subJobs);
              if (job.subJobs) {
                for (const subJob of job.subJobs) {
                  console.log('[OutputManager] SubJob:', subJob.id, 'external_job_id:', subJob.external_job_id, 'status:', subJob.status);
                  // Sub-jobs have status 'processing' when submitted to JSON2Video, or 'rendering' in some cases
                  if (subJob.external_job_id && (subJob.status === 'rendering' || subJob.status === 'processing')) {
                    projectsToResume.push({
                      id: subJob.external_job_id,
                      format: subJob.format,
                      status: 'processing' // Map to movieProject status
                    });
                  }
                }
              }
            }

            console.log('[OutputManager] Projects to resume:', projectsToResume.length, projectsToResume);
            if (projectsToResume.length > 0) {
              console.log('[OutputManager] Resuming movie status polling for projects:', projectsToResume);
              setMovieProjects(projectsToResume);
              setIsPollingMovieStatus(true);
            } else {
              console.log('[OutputManager] No projects to resume - subJobs may not have external_job_id or correct status');
            }
          }
        }
      } catch (fetchError: any) {
        console.error('[OutputManager] Error fetching export jobs:', fetchError);
      }
    };

    fetchRecentOutputs();
    fetchExportJobs();
  }, [activeSpreadsheetId]); // Re-fetch when sheet changes

  // Effect to fetch sheet name and set sheetDate, and baseFileNameForExport
  useEffect(() => {
    const fetchSheetDetails = async () => {
      if (activeSpreadsheetId) {
        // Now using weekly sheet directly instead of render sheet
        try {
          const name = await getSheetName(activeSpreadsheetId);
          if (name) {
            // Extract date from sheet name (assuming format like "YYYY-MM-DD Showdown")
            const dateMatch = name.match(/(\d{4}-\d{2}-\d{2})/); // Matches YYYY-MM-DD
            const simpleDateMatch = name.match(/(\d{1,2}-\d{1,2}-\d{2,4})/); // Matches M-D-YY or MM-DD-YYYY etc.

            let extractedDate = null;
            if (dateMatch) {
              extractedDate = dateMatch[1];
            } else if (simpleDateMatch) {
              extractedDate = simpleDateMatch[1];
            }
            setSheetDate(extractedDate);
            // Create a base filename from just the extracted date (e.g., "7-19-25")
            const baseFileName = extractedDate || 'unknown-date';
            setBaseFileNameForExport(baseFileName);
            console.log(`[OutputManager] Sheet Name: ${name}, Extracted Date: ${extractedDate}, Base Filename: ${baseFileName}`);
          }
        } catch (err) {
          console.error("[OutputManager] Error fetching sheet name:", err); // Corrected line
          setError("Failed to fetch sheet name."); // Corrected line
        }
      }
      setLoading(false);
    };
    fetchSheetDetails();
  }, [activeSpreadsheetId]); // Re-run if activeSpreadsheetId changes

  const handleRecentOutputClick = (url: string) => {
    console.log('[OutputManager] Recent output clicked, URL:', url);

    // Toggle behavior: if same URL clicked, hide the player
    if (selectedRecentVideoUrl === url) {
      setSelectedRecentVideoUrl(null);
    } else {
      setSelectedRecentVideoUrl(url);
    }

    // Mark as viewed locally for immediate dot dismissal
    setLocallyViewedUrls(prev => new Set(prev).add(url));

    // Persist to database (fire and forget)
    fetch(`${API_BASE_URL}/api/mark-output-viewed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ s3Url: url })
    }).catch(err => console.warn('[OutputManager] Failed to mark output as viewed:', err));
  };

  // Function to format timestamp with dashes (M-D-YY H:MM AM/PM)
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${month}-${day}-${year} ${hour12}:${minutes} ${ampm}`;
  };

  // Chart Date is stored inconsistently across older/newer rows — some as
  // "M-D-YY" (e.g. 8-22-26), some already spelled out ("August 22, 2026").
  // Normalize whatever is stored to the spelled-out form for display. Falls
  // back to the raw value if it can't be parsed (never blank a real value).
  const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const formatChartDate = (raw: string | null | undefined): string => {
    const value = (raw || '').trim();
    if (!value) return 'N/A';
    // Numeric "M-D-YY" or "M/D/YY" (also handles 4-digit years). Parse
    // explicitly — `new Date("8-22-26")` is unreliable across engines.
    const m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
    if (m) {
      const month = parseInt(m[1], 10);
      const day = parseInt(m[2], 10);
      let year = parseInt(m[3], 10);
      if (m[3].length === 2) year += 2000;
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return `${MONTH_NAMES[month - 1]} ${day}, ${year}`;
      }
    }
    // Already spelled out (or any other Date-parseable form) — round-trip it
    // so casing/spacing is consistent too.
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    }
    return value; // unparseable — show it as-is rather than lose it
  };

  // Parse a stored Chart Date (either "M-D-YY" or spelled out) to an epoch ms
  // for sorting, so mixed formats order chronologically instead of by raw
  // string. Unparseable values sort last.
  const chartDateToEpoch = (raw: string | null | undefined): number => {
    const value = (raw || '').trim();
    if (!value) return Number.NEGATIVE_INFINITY;
    const m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
    if (m) {
      let year = parseInt(m[3], 10);
      if (m[3].length === 2) year += 2000;
      return new Date(year, parseInt(m[1], 10) - 1, parseInt(m[2], 10)).getTime();
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? Number.NEGATIVE_INFINITY : d.getTime();
  };

  if (!activeSpreadsheetId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 4, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>No Chart Date Selected</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>Please select a date on the Charts page first.</Typography>
        <Button 
          onClick={() => navigate('/')} 
          variant="contained"
          sx={{ 
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Go to Charts
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isGenerating = isExporting;

  // Helper function for downloads
  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePlayVideo = (url: string, title: string) => {
    setVideoDialogUrl(url);
    setVideoDialogTitle(title);
    setVideoDialogOpen(true);
  };

  const handleCloseVideoDialog = () => {
    setVideoDialogOpen(false);
    setVideoDialogUrl('');
    setVideoDialogTitle('');
  };

  // Handler for copying S3 URL to clipboard
  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar('Link copied to clipboard!', { variant: 'success' });
    } catch (err) {
      console.error('[OutputManager] Failed to copy link:', err);
      enqueueSnackbar('Failed to copy link', { variant: 'error' });
    }
  };

  // Handler for sorting Output History table
  const handleSort = (column: 'timestamp' | 'chart' | 'sheetDate' | 'format') => {
    const isAsc = sortBy === column && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(column);
  };

  // Handler for deleting a render entry
  const handleDelete = async (timestamp: string, fileName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"? This will remove both the Google Sheets entry and the S3 file.`)) {
      return;
    }

    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/delete-render/${encodeURIComponent(timestamp)}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete render');
      }

      const result = await response.json();
      console.log('[OutputManager] Delete result:', result);

      // Refresh the recent outputs list
      const updatedOutputs = recentOutputs.filter(output => output.timestamp !== timestamp);
      setRecentOutputs(updatedOutputs);

      // Clear selected video if it was deleted
      if (selectedRecentVideoUrl && recentOutputs.find(o => o.timestamp === timestamp)?.s3Url === selectedRecentVideoUrl) {
        setSelectedRecentVideoUrl(null);
      }

      enqueueSnackbar('Render deleted successfully', { variant: 'success' });
    } catch (err) {
      console.error('[OutputManager] Failed to delete render:', err);
      enqueueSnackbar('Failed to delete render', { variant: 'error' });
    }
  };

  // Handler for deleting/dismissing an export job
  const handleDeleteExportJob = async (jobId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/export-manager/jobs/${jobId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete export job');
      }

      // Remove from local state
      setExportJobs(prev => prev.filter(job => job.id !== jobId));
      enqueueSnackbar('Export job dismissed', { variant: 'success' });
    } catch (err) {
      console.error('[OutputManager] Failed to delete export job:', err);
      enqueueSnackbar('Failed to dismiss export job', { variant: 'error' });
    }
  };

  const completedVideos = movieProjects.filter(p => p.status === 'finalized_s3' && p.url);
  const horizontalVideo = completedVideos.find(p => p.format.toLowerCase() === 'horizontal');
  const verticalVideo = completedVideos.find(p => p.format.toLowerCase() === 'vertical');

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 700, mx: 'auto', mb: 3 }}>
          <ToggleButtonGroup
            value={outputFormat}
            onChange={handleFormatChange}
            aria-label="output format"
            size="medium"
            sx={{
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: '#000000',
                color: themeColor,
                '&:hover': {
                  backgroundColor: '#333333',
                },
              },
              height: '46px', // Explicit height for the group
            }}
          >
            <ToggleButton value="horizontal" aria-label="horizontal format" sx={{ height: '100%' }}>
              Horizontal
            </ToggleButton>
            <ToggleButton value="vertical" aria-label="vertical format" sx={{ height: '100%' }}>
              Vertical
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Bilingual: render-language selector. Exports the selected language's
              VO; EN and ES produce distinct outputs (ES filenames carry _es_). */}
          {isBilingual && (
            <ToggleButtonGroup
              value={activeLanguage}
              exclusive
              size="medium"
              onChange={(_e, val) => { if (val) handleSetLanguage(val as VoLanguage); }}
              aria-label="render language"
              sx={{
                '& .MuiToggleButton-root.Mui-selected': {
                  backgroundColor: '#000000',
                  color: themeColor,
                  '&:hover': { backgroundColor: '#333333' },
                },
                height: '46px',
              }}
            >
              <ToggleButton value="en" aria-label="English" sx={{ height: '100%' }}>EN</ToggleButton>
              <ToggleButton value="es" aria-label="Spanish" sx={{ height: '100%' }}>ES</ToggleButton>
            </ToggleButtonGroup>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              startIcon={!isGenerating ? <AutoAwesomeIcon /> : undefined}
              onClick={isGenerating ? handleCancelGeneration : handleExport}
              disabled={ (loading || !!error || outputFormat.length === 0 || !isReadyForExport) && !isGenerating } // Added !isReadyForExport
              sx={{
                backgroundColor: isGenerating ? 'error.main' : themeColor,
                color: isGenerating ? 'error.contrastText' : '#000000',
                '&:hover': {
                  backgroundColor: isGenerating ? 'error.dark' : themeHoverColor,
                  color: isGenerating ? 'error.contrastText' :'#000000',
                },
                height: '46px',
              }}
            >
              {isGenerating ? 'CANCEL' : (isExporting ? 'Generating...' : 'GENERATE')}
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outlined"
              size="medium"
              sx={{
                textTransform: 'none',
                height: '46px', // Explicit height for this button
                borderColor: '#000000',
                color: '#000000',
                '&:hover': {
                  borderColor: '#000000',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              CHARTS
            </Button>
          </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      
      {showSuccessAlert && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setShowSuccessAlert(false)}>
          {alertMessage}
        </Alert>
      )}
      
      {/* Movie Rendering Status - Updated Alerts */}
      {movieProjects.filter(p => p.status !== 'finalized_s3').length > 0 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Final Video Rendering Status:</Typography>
          {movieProjects
            .filter(project => project.status !== 'finalized_s3') // Exclude already finalized videos
            .map((project) => {
              let severity: 'info' | 'success' | 'warning' | 'error' = 'info';
              let message = 'Processing...';

              if (project.status === 'done') {
                severity = 'info';
                message = 'Finalizing (saving to S3)...';
                if (project.url) {
                  message += ' '; // Add a space before the link
                }
              } else if (project.status === 'error') {
                severity = 'error';
                message = 'Failed to render. Check logs.';
              } else if (project.status === 'api_error' || project.status === 'network_error') {
                severity = 'warning';
                message = 'Communication error with render service.';
              } else if (project.status === 'processing' || project.status === 'rendering') {
                severity = 'info';
                message = 'Processing...';
              }
              // For any other statuses, it will default to 'info' and 'Processing...'

              return (
                <Alert
                  key={`${project.id}-${project.status}`}
                  severity={severity}
                  sx={{ mb: 1 }}
                >
                  {project.format.charAt(0).toUpperCase() + project.format.slice(1)} video: {message}
                  {project.status === 'done' && project.url && (
                    <Link href={project.url} target="_blank" rel="noopener noreferrer" sx={{ ml: 0.5 }}>
                      (View source render)
                    </Link>
                  )}
                </Alert>
              );
            })}
        </Box>
      )}

      {/* --- START: NEW VIDEO PLAYER SECTION --- */}
      {completedVideos.length > 0 && (
        <Box sx={{ mt: 3, mb: 3, border: '1px solid #ccc', borderRadius: '4px', p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {completedVideos.length > 1 ? 'Rendered Videos' : 'Rendered Video'}
          </Typography>
          {completedVideos.length > 1 && (horizontalVideo && verticalVideo) ? (
            <Box sx={{ width: '100%' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activePlayerTab} onChange={(event, newValue) => setActivePlayerTab(newValue)} aria-label="video player tabs">
                  {horizontalVideo && <Tab label="Horizontal (16:9)" />}
                  {verticalVideo && <Tab label="Vertical (9:16)" />}
                </Tabs>
              </Box>
              {horizontalVideo && activePlayerTab === 0 && (
                <Box sx={{ mt: 2 }}>
                  <video src={horizontalVideo.url} controls width="100%" style={{ aspectRatio: '16/9', maxHeight: '60vh' }} />
                </Box>
              )}
              {verticalVideo && activePlayerTab === (horizontalVideo ? 1 : 0) && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <video src={verticalVideo.url} controls style={{ height: '60vh', aspectRatio: '9/16' }} />
                </Box>
              )}
            </Box>
          ) : completedVideos.length === 1 && completedVideos[0].url ? (
            <Box sx={{ mt: 2 }}>
              <video 
                src={completedVideos[0].url} 
                controls 
                width={completedVideos[0].format.toLowerCase() === 'vertical' ? undefined : "100%"}
                style={{ 
                  aspectRatio: completedVideos[0].format.toLowerCase() === 'vertical' ? '9/16' : '16/9', 
                  height: completedVideos[0].format.toLowerCase() === 'vertical' ? '60vh' : undefined,
                  maxHeight: completedVideos[0].format.toLowerCase() === 'horizontal' ? '60vh' : undefined,
                  display: completedVideos[0].format.toLowerCase() === 'vertical' ? 'block' : undefined,
                  margin: completedVideos[0].format.toLowerCase() === 'vertical' ? '0 auto' : undefined,
                }} 
              />
            </Box>
          ) : null}

          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            {horizontalVideo && (
              <>
                <Button 
                  variant="outlined" 
                  onClick={() => handleDownload(horizontalVideo.url!, `${sheetDate || 'video'}_horizontal.mp4`)}
                >
                  Download 16:9
                </Button>
                {!verticalVideo && (
                  <Button 
                    variant="outlined" 
                    onClick={() => handleCopyLink(`Horizontal Video: ${horizontalVideo.url!}`)}
                  >
                    Copy Link
                  </Button>
                )}
              </>
            )}
            {verticalVideo && (
              <>
                <Button 
                  variant="outlined" 
                  onClick={() => handleDownload(verticalVideo.url!, `${sheetDate || 'video'}_vertical.mp4`)}
                >
                  Download 9:16
                </Button>
                {!horizontalVideo && (
                  <Button 
                    variant="outlined" 
                    onClick={() => handleCopyLink(`Vertical Video: ${verticalVideo.url!}`)}
                  >
                    Copy Link
                  </Button>
                )}
              </>
            )}
            {horizontalVideo && verticalVideo && (
              <>
                <Button
                  variant="contained"
                  onClick={() => {
                    console.log("[Download Both] Attempting Horizontal Download.");
                    console.log("[Download Both] Horizontal URL:", horizontalVideo.url);
                    console.log("[Download Both] Horizontal Filename:", `${sheetDate || 'video'}_horizontal.mp4`);
                    handleDownload(horizontalVideo.url!, `${sheetDate || 'video'}_horizontal.mp4`);
                    // Add a small delay before starting the second download
                    setTimeout(() => {
                      console.log("[Download Both] Attempting Vertical Download (after delay).");
                      console.log("[Download Both] Vertical URL:", verticalVideo.url);
                      console.log("[Download Both] Vertical Filename:", `${sheetDate || 'video'}_vertical.mp4`);
                      handleDownload(verticalVideo.url!, `${sheetDate || 'video'}_vertical.mp4`);
                    }, 500); // Increased delay to 500ms
                  }}
                >
                  Download Both
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const linksText = `Horizontal Video: ${horizontalVideo.url}\nVertical Video: ${verticalVideo.url}`;
                    handleCopyLink(linksText);
                  }}
                >
                  Copy Links
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}
      {/* --- END: NEW VIDEO PLAYER SECTION --- */}

      {/* DEBUG: Tease URL Display Dialog */}
      <Dialog
        open={debugDialogOpen}
        onClose={() => setDebugDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Tease URL Debug Information</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The following tease URLs were found from Google Sheets cells B84 and B85:
          </DialogContentText>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Horizontal Tease URL (B84):
            </Typography>
            {debugTeaseUrls.horizontal ? (
              <Button
                variant="outlined"
                href={debugTeaseUrls.horizontal}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mr: 1, mb: 1 }}
              >
                View Horizontal Tease
              </Button>
            ) : (
              <Typography color="text.secondary">No horizontal tease URL found</Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
              {debugTeaseUrls.horizontal || 'Empty'}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Vertical Tease URL (B85):
            </Typography>
            {debugTeaseUrls.vertical ? (
              <Button
                variant="outlined"
                href={debugTeaseUrls.vertical}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mr: 1, mb: 1 }}
              >
                View Vertical Tease
              </Button>
            ) : (
              <Typography color="text.secondary">No vertical tease URL found</Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
              {debugTeaseUrls.vertical || 'Empty'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDebugDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Error Modal */}
      <ValidationErrorModal
        open={validationDialogOpen}
        onClose={handleValidationDialogClose}
        errors={validationErrors}
        onNavigateToSection={(section) => {
          // Open destination in new tab so user can still see remaining validation errors
          if (section === 'chart') {
            window.open('/', '_blank');
          } else {
            // For intro/outro/tease, open the intro-outro page
            // Note: Can't pass state to new tab, so we use query params
            window.open(`/intro-outro/${activeSpreadsheetId}?section=${section}`, '_blank');
          }
        }}
      />

      {/* Active Export Jobs Section */}
      {exportJobs.filter(job => job.status === 'pending' || job.status === 'rendering').length > 0 && (
        <Box sx={{ width: '100%', maxWidth: 700, mx: 'auto', marginTop: 2, marginBottom: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'left', fontWeight: 'medium' }}>
            Active Export Jobs
          </Typography>
          <TableContainer component={Paper} sx={{ border: '1px solid #ddd', borderRadius: '4px', marginBottom: 2 }}>
            <Table sx={{ minWidth: 650 }} aria-label="active export jobs table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date/Time</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Chart Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Orientation</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 50 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exportJobs
                  .filter(job => job.status === 'pending' || job.status === 'rendering')
                  .map((job) => {
                    const formattedFormats = job.requested_formats?.map((f: string) => f.charAt(0).toUpperCase() + f.slice(1)).join(', ') || 'Unknown';
                    const createdTime = job.created_at
                      ? formatTimestamp(job.created_at)
                      : 'N/A';

                    return (
                      <TableRow key={job.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            {job.current_step || 'Processing...'}
                          </Box>
                        </TableCell>
                        <TableCell>{createdTime}</TableCell>
                        <TableCell>{job.sheet_date || 'N/A'}</TableCell>
                        <TableCell>{formattedFormats}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            aria-label="dismiss job"
                            onClick={() => handleDeleteExportJob(job.id)}
                            sx={{ color: 'text.secondary' }}
                            title="Dismiss"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Failed Export Jobs Section */}
      {exportJobs.filter(job => job.status === 'failed').length > 0 && (
        <Box sx={{ width: '100%', maxWidth: 700, mx: 'auto', marginTop: 2, marginBottom: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'left', fontWeight: 'medium', color: '#d32f2f' }}>
            Failed Exports
          </Typography>
          <TableContainer component={Paper} sx={{ border: '1px solid #ffcdd2', borderRadius: '4px', marginBottom: 2, backgroundColor: '#ffebee' }}>
            <Table sx={{ minWidth: 650 }} aria-label="failed export jobs table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Error</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date/Time</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Orientation</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 50 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exportJobs
                  .filter(job => job.status === 'failed')
                  .map((job) => {
                    const formattedFormats = job.requested_formats?.map((f: string) => f.charAt(0).toUpperCase() + f.slice(1)).join(', ') || 'Unknown';
                    const createdTime = job.created_at
                      ? formatTimestamp(job.created_at)
                      : 'N/A';

                    return (
                      <TableRow key={job.id}>
                        <TableCell sx={{ color: '#d32f2f', maxWidth: 300 }}>
                          {job.error_message || job.current_step || 'Export failed'}
                        </TableCell>
                        <TableCell>{createdTime}</TableCell>
                        <TableCell>{formattedFormats}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteExportJob(job.id)}
                            title="Dismiss"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Output History Section - Always Visible */}
      {recentOutputs && ( // Ensure recentOutputs is not null before trying to access its length or map over it
        <Box sx={{ width: '100%', maxWidth: 700, mx: 'auto', marginTop: 2, marginBottom: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'left', fontWeight: 'medium' }}>
            Output History
          </Typography>
          {selectedRecentVideoUrl && (
            <Box sx={{ marginBottom: 2, border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden' }}>
              <video key={selectedRecentVideoUrl} width="100%" controls>
                <source src={selectedRecentVideoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </Box>
          )}
          <TableContainer component={Paper} sx={{ border: '1px solid #ddd', borderRadius: '4px' }}>
            {recentOutputs.length > 0 ? (
              <Table sx={{ minWidth: 650 }} aria-label="output history table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortBy === 'timestamp'}
                        direction={sortBy === 'timestamp' ? sortOrder : 'asc'}
                        onClick={() => handleSort('timestamp')}
                      >
                        Date/Time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortBy === 'chart'}
                        direction={sortBy === 'chart' ? sortOrder : 'asc'}
                        onClick={() => handleSort('chart')}
                      >
                        Chart
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortBy === 'sheetDate'}
                        direction={sortBy === 'sheetDate' ? sortOrder : 'asc'}
                        onClick={() => handleSort('sheetDate')}
                      >
                        Chart Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortBy === 'format'}
                        direction={sortBy === 'format' ? sortOrder : 'asc'}
                        onClick={() => handleSort('format')}
                      >
                        Orientation
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...recentOutputs]
                    .sort((a, b) => {
                      let aVal: any;
                      let bVal: any;

                      if (sortBy === 'timestamp') {
                        aVal = new Date(a.timestamp || 0).getTime();
                        bVal = new Date(b.timestamp || 0).getTime();
                      } else if (sortBy === 'chart') {
                        aVal = a.chart || '';
                        bVal = b.chart || '';
                      } else if (sortBy === 'sheetDate') {
                        aVal = chartDateToEpoch(a.sheetDate);
                        bVal = chartDateToEpoch(b.sheetDate);
                      } else {
                        aVal = a.format || '';
                        bVal = b.format || '';
                      }

                      if (sortOrder === 'asc') {
                        return aVal > bVal ? 1 : -1;
                      } else {
                        return aVal < bVal ? 1 : -1;
                      }
                    })
                    .map((output, index) => (
                      <TableRow
                        key={output.s3Url || index}
                        onClick={() => {
                          console.log('[OutputManager] TableRow onClick triggered for URL:', output.s3Url);
                          handleRecentOutputClick(output.s3Url);
                        }}
                        sx={{
                          backgroundColor: selectedRecentVideoUrl === output.s3Url ? 'lightblue' : (index % 2 === 0 ? 'rgba(0, 0, 0, 0.04)' : 'transparent'),
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: selectedRecentVideoUrl === output.s3Url ? 'lightblue' : 'rgba(0, 0, 0, 0.08)',
                          },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              aria-label="download"
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('[OutputManager] Download icon clicked for:', output.fileName);
                                handleDownload(output.s3Url, output.fileName);
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="copy link"
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('[OutputManager] Copy link clicked for:', output.fileName);
                                handleCopyLink(output.s3Url);
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="delete render"
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('[OutputManager] Delete icon clicked for:', output.fileName);
                                handleDelete(output.timestamp, output.fileName);
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {!output.viewed && !locallyViewedUrls.has(output.s3Url) && (
                              <Box
                                sx={{
                                  backgroundColor: themeColor,
                                  borderRadius: '50%',
                                  width: '8px',
                                  height: '8px',
                                  flexShrink: 0,
                                }}
                                title="New Output"
                              />
                            )}
                            {output.timestamp ? formatTimestamp(output.timestamp) : 'No timestamp'}
                          </Box>
                        </TableCell>
                        <TableCell>{(output.chart || 'Hot 100').replace(/^Billboard\s+/i, '')}</TableCell>
                        <TableCell>{formatChartDate(output.sheetDate)}</TableCell>
                        <TableCell>{output.format ? output.format.charAt(0).toUpperCase() + output.format.slice(1) : 'N/A'}</TableCell>
                      </TableRow>
                    ))
                  }
                </TableBody>
              </Table>
            ) : (
              <Typography sx={{ textAlign: 'center', padding: 2, color: 'text.secondary' }}>
                No output history available.
              </Typography>
            )}
          </TableContainer>
        </Box>
      )}

      {/* Video Popup Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={handleCloseVideoDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#000',
            margin: 2,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', textAlign: 'center', pb: 1 }}>
          {videoDialogTitle}
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {videoDialogUrl && (
            <video
              src={videoDialogUrl}
              controls
              autoPlay
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={handleCloseVideoDialog} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', width: '100%', paddingTop: '0px' }}>
        {/* Add your main content components here */}
      </Box>
    </Box>
  );
};